import { Route, Routes } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicOnlyRoute from "./components/PublicOnlyRoute";
import About from "./pages/About";
import Roadmap from "./pages/Roadmap";
import ForgotPassword from "./pages/ForgotPassword";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/seeker/Dashboard";
import Home from "./pages/seeker/Home";
import Login from "./pages/seeker/Login";
import Register from "./pages/seeker/Register";
import Settings from "./pages/seeker/Settings";
import VerifyEmail from "./pages/VerifyEmail";
import VerifyPending from "./pages/VerifyPending";
import EmployerRegister from "./pages/employer/EmployerRegister";
import EmployerLogin from "./pages/employer/EmployerLogin";
import EmployerVerifyPending from "./pages/employer/EmployerVerifyPending";
import EmployerVerifyEmail from "./pages/employer/EmployerVerifyEmail";
import EmployerDashboard from "./pages/employer/EmployerDashboard";
import EmployerArena from "./pages/employer/EmployerArena";
import JobPositionsList from "./pages/employer/JobPositionsList";
import JobPositionWizard from "./pages/employer/JobPositionWizard";
import ResumeBuilder from "./pages/seeker/ResumeBuilder";
import Inbox from "./pages/seeker/Inbox";
import PublicResume from "./pages/PublicResume";
import EmployerJoin from "./pages/employer/EmployerJoin";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import ApiDocs from "./pages/ApiDocs";
import CookieBanner from "./components/CookieBanner";
import EmployerSettings from "./pages/employer/EmployerSettings";
import HowItWorks from "./pages/HowItWorks";
import ConnectLanding from "./pages/ConnectLanding";
import RefundPolicy from "./pages/RefundPolicy";
import ArenaApiDocs from "./pages/ArenaApiDocs";
import EmployerLanding from "./pages/employer/EmployerLanding";
import AgencyLanding from "./pages/employer/AgencyLanding";
import AgencyCandidates from "./pages/employer/AgencyCandidates";
import LabourMarketIndex from "./pages/LabourMarketIndex";
import SubmitToPosition from "./pages/SubmitToPosition";
import PublicShareView from "./pages/PublicShareView";
import JobPositionDetail from "./pages/employer/JobPositionDetail";
import AdminDashboard from "./pages/seeker/AdminDashboard";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import Arena from "./pages/seeker/Arena";

import CCRMockupV2 from "./pages/employer/CCRMockupV2";

import { useState, useEffect } from "react";
import api from "./api/client";
import useAuthStore from "./store/authStore";
import LoadingSpinner from "./components/LoadingSpinner";

export default function App() {
  const [ready, setReady] = useState(false);
  const { setSeekerAuth, setEmployerAuth } = useAuthStore();

  useEffect(() => {
    const restore = async () => {
      // Try to restore seeker session
      try {
        const { data } = await api.post("/auth/refresh");
        setSeekerAuth(data.access_token, data.user);
      } catch {}

      // Try to restore employer session
      try {
        const { data } = await api.post("/employer/auth/refresh");
        setEmployerAuth(data.access_token, data.employer);
      } catch {}

      setReady(true);
    };
    restore();
  }, []);

  if (!ready)
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-brand-dark-bg">
        <LoadingSpinner />
      </div>
    );

  return (
    <>
      <Routes>
        {/* ── Public ── */}
        <Route path="/" element={<Landing />} />
        <Route path="/employers/how-it-works" element={<EmployerLanding />} />
        <Route path="/employers/agency" element={<AgencyLanding />} />
        <Route path="/about" element={<About />} />
        <Route path="/roadmap" element={<Roadmap />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/employers/api" element={<ApiDocs />} />
        <Route path="/employers/arena-api" element={<ArenaApiDocs />} />
        <Route path="/labour-market-index" element={<LabourMarketIndex />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-pending" element={<VerifyPending />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/connect/:token" element={<ConnectLanding />} />
        <Route path="/submit/:token" element={<SubmitToPosition />} />
        <Route path="/share/:shareToken" element={<PublicShareView />} />
        <Route path="/r/:slug" element={<PublicResume />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        {/* ── Seeker auth (public-only: redirect if already logged in) ── */}
        <Route element={<PublicOnlyRoute role="seeker" />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
        </Route>
        {/* ── Seeker protected ── */}
        <Route element={<ProtectedRoute role="seeker" />}>
          <Route path="/app/dashboard" element={<Dashboard />} />
          <Route path="/app/arena" element={<Arena />} />
          <Route path="/app/settings" element={<Settings />} />
          <Route path="/app/settings" element={<Settings />} />
          <Route path="/app/home" element={<Home />} />
          <Route path="/app/resume" element={<ResumeBuilder />} />
          <Route path="/app/inbox" element={<Inbox />} />
          <Route path="/app/admin" element={<AdminDashboard />} />
        </Route>
        {/* ── Employer auth (public-only) ── */}
        <Route element={<PublicOnlyRoute role="employer" />}>
          <Route path="/employers/login" element={<EmployerLogin />} />
          <Route path="/employers/register" element={<EmployerRegister />} />
        </Route>
        {/* ── Employer public (always accessible) ── */}
        <Route
          path="/employers/verify-pending"
          element={<EmployerVerifyPending />}
        />
        <Route
          path="/employers/verify-email"
          element={<EmployerVerifyEmail />}
        />
        <Route path="/employer/ccr-mockup" element={<CCRMockupV2 />} />
        <Route path="/employers/join" element={<EmployerJoin />} />
        {/* ── Employer protected ── */}
        <Route element={<ProtectedRoute role="employer" />}>
          <Route path="/employers/dashboard" element={<EmployerDashboard />} />
          <Route path="/employers/arena" element={<EmployerArena />} />
          <Route path="/employers/positions" element={<JobPositionsList />} />
          <Route
            path="/employers/positions/new"
            element={<JobPositionWizard />}
          />
          <Route
            path="/employers/positions/:positionId"
            element={<JobPositionDetail />}
          />
          <Route
            path="/employers/positions/:positionId/edit"
            element={<JobPositionWizard />}
          />
          <Route path="/employers/settings" element={<EmployerSettings />} />
          <Route path="/employers/candidates" element={<AgencyCandidates />} />
        </Route>
        {/* ── Fallback ── */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <CookieBanner />
    </>
  );
}
